"use client";

import { type AriaTextFieldOptions, useTextField } from "@react-aria/textfield";
import { useObjectRef } from "@react-aria/utils";
import type { VariantProps } from "class-variance-authority";
import { type ChangeEvent, type ReactNode, type Ref, useEffect } from "react";
import ReactTextareaAutosize, { type TextareaAutosizeProps } from "react-textarea-autosize";

import { cn } from "../../utils";
import { useTextFieldRootContext } from "./context";
import { inputContainerStyles, inputStyles } from "./style";

export type TextFieldTextareaProps = {
  className?: string;
  containerClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  isWarning?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
} & Omit<TextareaAutosizeProps, "prefix"> &
  Omit<AriaTextFieldOptions<"textarea">, "inputElementType" | "onChange"> &
  VariantProps<typeof inputContainerStyles>;

const Textarea = ({
  ref: forwardedRef,
  className,
  containerClassName,
  prefix,
  suffix,
  disabled,
  readOnly,
  onChange,
  isInvalid,
  isWarning,
  variant = "outline",
  color = "primary",
  ...props
}: TextFieldTextareaProps) => {
  const ref = useObjectRef(forwardedRef);
  const { setTextField } = useTextFieldRootContext<"textarea">();

  const textField = useTextField(
    {
      ...props,
      isReadOnly: readOnly,
      isDisabled: disabled,
      inputElementType: "textarea",
      // https://github.com/adobe/react-spectrum/issues/1860#issuecomment-849833808
      onChange: (value: string) =>
        onChange && onChange({ target: { value, name: props.name } } as ChangeEvent<HTMLTextAreaElement>),
      label: true,
      isInvalid,
    },
    ref
  );

  const { inputProps, isInvalid: isInvalidTextField } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalidTextField]);

  const handleFocus = () => ref.current?.focus();

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    <div
      className={cn(inputContainerStyles({ variant, color }), "h-fit min-h-11", containerClassName)}
      data-invalid={isInvalid}
      data-warning={isWarning}
      data-disabled={inputProps.disabled}
      role="button"
      tabIndex={-1}
      onKeyUp={() => null}
      onClick={handleFocus}
    >
      {prefix}

      <ReactTextareaAutosize
        {...props}
        {...inputProps}
        className={cn(inputStyles({ variant }), className)}
        style={inputProps.style as TextFieldTextareaProps["style"]}
        ref={ref}
      />

      {suffix}
    </div>
  );
};

Textarea.displayName = "Textarea";

export default Textarea;
